<template>
  <div class="app">
    <div id="cesiumContainer"></div>

    <AppNav :active-id="activePageId" />

    <FeaturePopup :picked="map.picked" @close="map.picked = null" />

    <div
      v-if="map.hoverTip"
      class="polder-tip"
      :style="{ left: map.hoverTip.x + 'px', top: map.hoverTip.y + 'px' }"
    >
      {{ map.hoverTip.text }}
    </div>

    <div class="loader" v-if="loading">
      <div class="loader__card">
        <div class="loader__title">系统初始化</div>
        <div class="loader__bar">
          <i :style="{ width: loadPercent + '%' }"></i>
        </div>
        <div class="loader__msg">{{ loadMessage }}</div>
      </div>
    </div>

    <router-view v-if="mapReady" />
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import "cesium/Build/Cesium/Widgets/widgets.css";
import AppNav from "@/components/AppNav.vue";
import FeaturePopup from "@/components/FeaturePopup.vue";
import { createSharedViewer } from "@/cesium/createViewer";
import {
  createMapStore,
  provideMapStore,
} from "@/composables/useMapStore";

const route = useRoute();
const map = createMapStore();
provideMapStore(map);

const loading = ref(true);
const loadPercent = ref(8);
const loadMessage = ref("创建三维场景…");
const mapReady = ref(false);

const activePageId = computed(
  () => route.meta.pageId || map.activePage || "home"
);

onMounted(() => {
  const viewer = createSharedViewer("cesiumContainer");
  map.setViewer(viewer);
  loadPercent.value = 100;
  loadMessage.value = "就绪";
  mapReady.value = true;
  setTimeout(() => {
    loading.value = false;
  }, 280);
});

onBeforeUnmount(() => {
  // 必须在 Vue 拆掉 canvas DOM 之前停掉渲染并销毁，否则会 VertexArray destroyed
  map.destroy({ destroyViewer: true });
});
</script>

<style>
@import url("https://fonts.googleapis.com/css2?family=Orbitron:wght@500;700&family=Noto+Sans+SC:wght@400;500;700&display=swap");

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.app {
  --cyan: #3de7ff;
  --cyan-dim: rgba(61, 231, 255, 0.35);
  --panel: rgba(8, 28, 48, 0.68);
  --line: rgba(61, 231, 255, 0.22);
  --text: #d8f6ff;
  --muted: rgba(216, 246, 255, 0.55);
  position: relative;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  font-family: "Noto Sans SC", "PingFang SC", sans-serif;
  color: var(--text);
  background:
    radial-gradient(ellipse 80% 50% at 50% -10%, rgba(20, 60, 90, 0.45), transparent 60%),
    #02060c;
}

#cesiumContainer {
  width: 100%;
  height: 100%;
}

.loader {
  position: absolute;
  inset: 0;
  z-index: 50;
  display: grid;
  place-items: center;
  background: rgba(2, 6, 12, 0.72);
  backdrop-filter: blur(6px);
}

.loader__card {
  width: min(360px, 80vw);
  padding: 28px 32px;
  border: 1px solid var(--line);
  background: var(--panel);
}

.loader__title {
  font-family: "Orbitron", sans-serif;
  font-size: 14px;
  letter-spacing: 0.2em;
  margin-bottom: 16px;
  color: var(--cyan);
}

.loader__bar {
  height: 3px;
  background: rgba(255, 255, 255, 0.08);
  overflow: hidden;
}

.loader__bar i {
  display: block;
  height: 100%;
  background: linear-gradient(90deg, #0aa, var(--cyan));
  transition: width 0.35s ease;
}

.loader__msg {
  margin-top: 12px;
  font-size: 12px;
  color: var(--muted);
}

.polder-tip {
  position: fixed;
  z-index: 70;
  transform: translate(-50%, -50%);
  padding: 6px 12px;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.06em;
  color: #fff;
  white-space: nowrap;
  pointer-events: none;
  background: rgba(2, 14, 26, 0.88);
  border: 1px solid rgba(61, 231, 255, 0.55);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.45);
}
</style>
